import { FunctionComponent, ReactNode } from "react";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { OpenInNew } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { managementTouchIconButtonSx } from "../utils/management-touch-target.styles";

/** Larger section heading so the Today panel reads as the current-day overview. */
export const managementSectionCardProminentTitleSx: SystemStyleObject<Theme> = {
  fontWeight: 700,
  fontSize: { xs: "1.25rem", md: "1.375rem" },
  lineHeight: 1.2,
  letterSpacing: "-0.02em"
};

type ManagementSectionCardProps = {
  title?: string;
  /** Secondary line under the title (e.g. filter / period scope). */
  subtitle?: string;
  /** Extra title styles (e.g. a larger Today heading). */
  titleSx?: SystemStyleObject<Theme>;
  titleTestId?: string;
  drillTo?: string;
  drillAriaLabel?: string;
  /** Unique test id for the drill control (avoid shared `management-section-drill`). */
  drillTestId?: string;
  children: ReactNode;
  minHeight?: number;
};

/**
 * Outlined metric panel with optional top-right drill-through icon button.
 */
export const ManagementSectionCard: FunctionComponent<ManagementSectionCardProps> = ({
  title,
  subtitle,
  titleSx,
  titleTestId,
  drillTo,
  drillAriaLabel,
  drillTestId = "management-section-drill",
  children,
  minHeight = 200
}) => {
  const showHeader = Boolean(title) || Boolean(drillTo);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: "8px",
        height: "100%",
        minHeight,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper"
      }}
    >
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 2
          }}
        >
          {title ? (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle1"
                component="h2"
                data-testid={titleTestId}
                sx={[{ fontWeight: 600 }, titleSx ?? {}]}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.25 }}
                  data-testid="management-section-subtitle"
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          {drillTo && (
            <IconButton
              component={RouterLink}
              to={drillTo}
              aria-label={drillAriaLabel ?? title}
              data-testid={drillTestId}
              sx={managementTouchIconButtonSx}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{children}</Box>
    </Paper>
  );
};
